/**
 * Says what a trigger does, in a sentence a person can check.
 *
 * A trigger's stored shape — `{events, condition, once_per, targets}` — is
 * four independent fields, and reading it back as four labelled boxes leaves
 * the reader to assemble the meaning themselves. Every surface that shows a
 * trigger (the list row, the read view, a confirmation before saving) wants
 * the same assembled sentence, so it is built once here, as data rather than
 * as markup: prose comes back as strings the caller renders, and each target
 * comes back as its own spec so the caller can link it.
 */

import cronstrue from "cronstrue";

import { fileSelectorsOf, type InvocationInput } from "@/shared/domain/actions";
import { type ListFilterComparator } from "@/shared/domain/query/filters";
import {
  conditionToList,
  type ListGroup,
} from "@/shared/domain/query/listGroups";
import { NOT_SET_LABEL } from "@/shared/domain/query/notSet";
import { formatBoolean } from "@/shared/text";
import { capitalizeFirst } from "@/shared/text/capitalizeFirst";
import { type Comparator, type Condition } from "@/types";

import { ACCEPT_ALL_PATTERN } from "./acceptAllPattern";
import { DatasetBinding } from "./datasetBinding";
import {
  actionTakesTheFile,
  entityOfSubscription,
  type PlatformEntity,
} from "./entities";
import { eventLabel, variableRoots } from "./eventsCatalog";
import { templateTypeOf } from "./templateCatalog";
import {
  isSchedule,
  isTriggeringSession,
  OncePer,
  PlatformEventType,
  TriggerTargetType,
  type TriggerSource,
  type InvokeActionTarget,
  type TriggerTargetSpec,
  type TriggerRecord,
} from "./TriggerRecord";

/**
 * How each event type reads inside "When ___". Written as a clause with a
 * subject, not as the picker's noun label ("File uploaded"), because the
 * sentence has to survive being read aloud.
 */
const EVENT_CLAUSES: Record<PlatformEventType, string> = {
  [PlatformEventType.FileUploaded]: "a file is uploaded",
  [PlatformEventType.UploadCompleted]: "an upload finishes",
  [PlatformEventType.FileIngested]: "a file finishes ingesting",
  [PlatformEventType.FileMetadataUpdated]: "a file's metadata changes",
  [PlatformEventType.DatasetMetadataUpdated]: "a dataset's metadata changes",
  [PlatformEventType.DatasetCreated]: "a dataset is created",
  [PlatformEventType.DatasetTagAdded]: "a tag is added to a dataset",
  [PlatformEventType.InvocationCompleted]: "an action invocation completes",
  [PlatformEventType.InvocationFailed]: "an action invocation fails",
  [PlatformEventType.SessionIngested]: "a session is fully ingested",
  [PlatformEventType.EventCreated]: "an event is created on a dataset",
  [PlatformEventType.SessionEventCreated]: "an event is created on a session",
  [PlatformEventType.ScheduleFired]: "a scheduled minute arrives",
};

/**
 * How each comparator reads after a field name, in both numbers.
 *
 * Both forms are needed because the subject is the field, and fields are as
 * often plural as singular: "the dataset's tags contain" and "the file's path
 * contains" are both correct, and picking one form makes half the conditions
 * read as broken English. The negatives are spelled out rather than repeating
 * the wire spelling, which is the sort of thing a reader skims past without
 * noticing the "NOT".
 */
const COMPARATOR_PHRASES: Record<
  Comparator,
  { singular: string; plural: string }
> = {
  EQUALS: { singular: "is", plural: "are" },
  NOT_EQUALS: { singular: "is not", plural: "are not" },
  CONTAINS: { singular: "contains", plural: "contain" },
  NOT_CONTAINS: { singular: "does not contain", plural: "do not contain" },
  GREATER_THAN: { singular: "is greater than", plural: "are greater than" },
  LESS_THAN: { singular: "is less than", plural: "are less than" },
  GREATER_THAN_OR_EQUAL: { singular: "is at least", plural: "are at least" },
  LESS_THAN_OR_EQUAL: { singular: "is at most", plural: "are at most" },
  IS_NULL: { singular: "is not set", plural: "are not set" },
  IS_NOT_NULL: { singular: "is set", plural: "are set" },
  BEGINS_WITH: { singular: "begins with", plural: "begin with" },
  EXISTS: { singular: "is set", plural: "are set" },
  NOT_EXISTS: { singular: "is not set", plural: "are not set" },
  LIKE: { singular: "matches", plural: "match" },
  NOT_LIKE: { singular: "does not match", plural: "do not match" },
};

/** How a list row reads, in the same two numbers as {@link COMPARATOR_PHRASES}. */
const LIST_PHRASES: Record<
  ListFilterComparator,
  { singular: string; plural: string }
> = {
  ANY_OF: { singular: "is any of", plural: "are any of" },
  NONE_OF: { singular: "is none of", plural: "are none of" },
};

/**
 * Field names that end in "s" without being plural. The heuristic below is
 * crude on purpose -- a full inflection library would be a lot of weight for
 * one verb -- so the exceptions that actually occur in the namespace are
 * listed rather than inferred.
 */
const SINGULAR_ENDING_IN_S: ReadonlySet<string> = new Set([
  "status",
  "address",
  "progress",
  "metadata",
  "class",
  "access",
]);

function isPluralField(field: string): boolean {
  const leaf = field.split(".").pop() ?? "";
  return leaf.endsWith("s") && !SINGULAR_ENDING_IN_S.has(leaf);
}

/** Comparators that stand alone — appending a value would read as nonsense. */
const VALUELESS_COMPARATORS: ReadonlySet<Comparator> = new Set<Comparator>([
  "IS_NULL",
  "IS_NOT_NULL",
  "EXISTS",
  "NOT_EXISTS",
]);

/** One target's clause: the verb and the target it acts on. */
export interface TriggerOutcome {
  /** "Invoke", "Start agent", "Notify". */
  verb: string;
  /** The spec, so the caller can link the action, agent, or channel. */
  target: TriggerTargetSpec;
}

/**
 * How often the trigger may fire for the same subject, split at its first word
 * so that word can stand in the sentence's connector column: "Once" / "per
 * file", or "Every" / "time it happens".
 */
export interface TriggerGrain {
  keyword: "Once" | "Every";
  clause: string;
}

export interface TriggerSummary {
  /** The clause after "When", e.g. "a file is uploaded". */
  when: string;
  /** How often it may fire for the same subject; null on a schedule. */
  grain: TriggerGrain | null;
  /**
   * The clause after "and", e.g. "the dataset's tags contain \"validated\"",
   * or null when the trigger fires on every matching platform event.
   */
  condition: string | null;
  /** One clause per target, in dispatch order. */
  outcomes: TriggerOutcome[];
}

function isGroup(
  condition: Condition,
): condition is Extract<Condition, { conditions: unknown }> {
  return "conditions" in condition;
}

/**
 * Reads a namespace path as English: `dataset.tags` becomes "the dataset's
 * tags", `file.relative_path` becomes "the file's relative path".
 *
 * Metadata paths keep their key verbatim (`dataset.metadata.vehicle_id` →
 * "the dataset's metadata vehicle_id") because the key is the customer's own
 * word and prettifying it would stop it matching what they typed.
 */
export function readableField(field: string): string {
  const [root, ...rest] = field.split(".");
  if (rest.length === 0) {
    return humanizeSegments(rest.concat(root));
  }
  return `the ${root}'s ${humanizeSegments(rest)}`;
}

/**
 * Joins path segments into words, turning underscores into spaces -- except
 * past a ``metadata`` segment, where the rest is a customer-authored key.
 * Prettifying that would stop it matching what they typed into the builder.
 */
function humanizeSegments(segments: string[]): string {
  const metadataAt = segments.indexOf("metadata");
  if (metadataAt === -1) {
    return segments.join(" ").replace(/_/g, " ");
  }
  const before = segments
    .slice(0, metadataAt + 1)
    .join(" ")
    .replace(/_/g, " ");
  const key = segments.slice(metadataAt + 1).join(".");
  return key.length > 0 ? `${before} ${key}` : before;
}

/** A non-string condition value as display text; a boolean reads "True" / "False". */
function displayScalar(value: unknown): string {
  return typeof value === "boolean" ? formatBoolean(value) : String(value);
}

function readableValue(value: boolean | number | string): string {
  return typeof value === "string" ? `"${value}"` : displayScalar(value);
}

/**
 * A list row's members, comma-joined, with "Not set" listed first, as the
 * filter chip lists it.
 */
function listMembers(list: ListGroup): string {
  return [
    ...(list.includeNull ? [NOT_SET_LABEL] : []),
    ...list.values.map(displayScalar),
  ].join(", ");
}

/**
 * Reads a condition tree as English.
 *
 * Groups are parenthesized only when nested, so the common flat AND reads as a
 * plain list while a genuinely nested shape stays unambiguous. A trigger whose
 * condition the builder cannot edit still summarizes here — the reader should
 * be able to see what it does even when they have to leave to change it.
 */
export function readableCondition(condition: Condition, depth = 0): string {
  if (isGroup(condition)) {
    if (condition.operator === "NOT") {
      const inner = condition.conditions
        .map((child) => readableCondition(child, depth + 1))
        .join(" and ");
      return `not (${inner})`;
    }
    const joiner = condition.operator === "OR" ? " or " : " and ";
    const parts = condition.conditions.map((child) =>
      readableCondition(child, depth + 1),
    );
    const joined = parts.join(joiner);
    return depth > 0 && parts.length > 1 ? `(${joined})` : joined;
  }

  const forms = COMPARATOR_PHRASES[condition.comparator];
  const phrase = forms
    ? forms[isPluralField(condition.field) ? "plural" : "singular"]
    : condition.comparator;
  if (VALUELESS_COMPARATORS.has(condition.comparator)) {
    return `${readableField(condition.field)} ${phrase}`;
  }
  return `${readableField(condition.field)} ${phrase} ${readableValue(condition.value)}`;
}

/**
 * One line of a condition, as a surface that lists them row by row needs it.
 *
 * A comparison comes back in three pieces so the caller can weight them --
 * the field is what the reader is looking for, the value is what they are
 * checking it against, and the comparator between them is grammar. A shape
 * the three pieces cannot hold (an OR, a NOT, anything nested) comes back as
 * the one string `readableCondition` already produces, rather than being
 * flattened into a comparison it is not.
 */
export type TriggerClause =
  | {
      kind: "comparison";
      /** The field read as English, e.g. "the dataset's description". */
      field: string;
      /** How it is compared, e.g. "is less than". */
      comparator: string;
      /** What it is compared against; null for a comparator that stands alone. */
      value: ClauseValue | null;
    }
  | { kind: "expression"; text: string };

/**
 * A comparison's right-hand side.
 *
 * An instant is kept as the stored ISO string rather than rendered here: a
 * timestamp is written in UTC and read in whatever zone and format the person
 * looking at it has chosen, and only the caller knows that.
 */
export type ClauseValue =
  { kind: "text"; text: string } | { kind: "instant"; iso: string };

/**
 * Whether the value is a timestamp, decided by the same ISO-8601 test that
 * types the field in the first place -- a condition offers a date picker for
 * exactly the paths whose sample `templateTypeOf` reads as a date, so asking
 * it about the stored value gives the builder's own answer back.
 */
function clauseValue(value: boolean | number | string): ClauseValue {
  return typeof value === "string" && templateTypeOf(value) === "date"
    ? { kind: "instant", iso: value }
    : { kind: "text", text: displayScalar(value) };
}

function clauseOf(condition: Condition): TriggerClause {
  if (isGroup(condition)) {
    const list = conditionToList(condition);
    if (list === null) {
      return { kind: "expression", text: readableCondition(condition) };
    }
    return {
      kind: "comparison",
      field: readableField(list.field),
      comparator:
        LIST_PHRASES[list.comparator][
          isPluralField(list.field) ? "plural" : "singular"
        ],
      value: { kind: "text", text: listMembers(list) },
    };
  }
  const forms = COMPARATOR_PHRASES[condition.comparator];
  return {
    kind: "comparison",
    field: readableField(condition.field),
    comparator: forms
      ? forms[isPluralField(condition.field) ? "plural" : "singular"]
      : condition.comparator,
    value: VALUELESS_COMPARATORS.has(condition.comparator)
      ? null
      : clauseValue(condition.value),
  };
}

/**
 * A condition as the rows that make it up.
 *
 * Only a top-level AND splits: its children are requirements that each hold
 * independently, which is exactly what a list of rows claims. An OR is one
 * requirement however many alternatives it offers, so it stays one row -- and
 * splitting it would read as an AND, which is the opposite of what it means.
 * A nested group is one row, as in the trigger builder, so the one-child AND
 * the builder wraps a lone AND-shaped row in reads as that row. A group that
 * spells an "is any of" / "is none of" list reads as the list.
 */
export function conditionClauses(
  condition: Condition | null | undefined,
): TriggerClause[] {
  if (condition === null || condition === undefined) {
    return [];
  }
  if (isGroup(condition) && condition.operator === "AND") {
    return condition.conditions.map(clauseOf);
  }
  return [clauseOf(condition)];
}

/**
 * Reads an event subscription as English. Multiple events are joined with
 * "or" — the trigger evaluates on any of them, and "and" would read as
 * requiring all of them.
 */
export function readableEvents(events: readonly PlatformEventType[]): string {
  const clauses = events.map(
    (eventType) => EVENT_CLAUSES[eventType] ?? eventLabel(eventType),
  );
  if (clauses.length === 0) {
    return "nothing happens";
  }
  if (clauses.length === 1) {
    return clauses[0];
  }
  const last = clauses[clauses.length - 1];
  return `${clauses.slice(0, -1).join(", ")} or ${last}`;
}

export { ACCEPT_ALL_PATTERN };

/** Which selector a stored invocation input carries, if any. */
export function invocationQueryNoun(
  input: InvocationInput | null | undefined,
): "files" | "topics" | null {
  if (input === null || input === undefined) {
    return null;
  }
  const topics = Array.isArray(input.topics) ? input.topics[0] : input.topics;
  if (topics?.query) {
    return "topics";
  }
  return fileSelectorsOf(input)[0]?.query ? "files" : null;
}

/**
 * What an invocation gets from this trigger, as the phrase after the action's
 * name. The runtime binds an event-fired invocation to the event's dataset
 * (gated and mounted by the patterns, extended by a query). An event that
 * carries no dataset, and a schedule, give the invocation only what its input
 * selects: the session that changed, a query, or a schedule's dataset binding.
 */
export function invocationInputPhrase({
  firesOnSchedule,
  entity,
  hasDataset,
  passesSession,
  oncePerFile,
  requiredInputs,
  queryNoun,
  dataset,
}: {
  firesOnSchedule: boolean;
  /** The one entity the trigger watches, when it watches one. */
  entity: PlatformEntity | null;
  /** Whether every subscribed event carries a dataset (true for a mixed subscription that shares one). */
  hasDataset: boolean;
  /** The firing is about a session, and the input selects that session. */
  passesSession: boolean;
  /** The trigger fires once per file: the invocation gets just that file. */
  oncePerFile: boolean;
  requiredInputs: readonly string[];
  queryNoun: "files" | "topics" | null;
  /** A schedule-fired target's dataset, when it is bound to one. */
  dataset: DatasetBinding | null;
}): string {
  if (firesOnSchedule) {
    if (dataset !== null) {
      return dataset.patterns.length === 0
        ? `Dataset ${dataset.datasetId}`
        : `Dataset ${dataset.datasetId}, mounting ${dataset.patterns.join(", ")}`;
    }
    return queryNoun === null
      ? "No inputs"
      : `${capitalizeFirst(queryNoun)} selected by the query`;
  }
  if (!hasDataset) {
    const given = passesSession ? ["The session that changed"] : [];
    if (queryNoun !== null) {
      given.push(
        given.length === 0
          ? `${capitalizeFirst(queryNoun)} selected by the query`
          : `plus ${queryNoun} selected by the query`,
      );
    }
    return given.length === 0
      ? "Nothing \u2014 these platform events carry no dataset"
      : given.join(", ");
  }
  const patterns = requiredInputs.filter((p) => p.trim().length > 0);
  const parts: string[] = [];
  if (oncePerFile) {
    parts.push("Just the file that changed");
    // The accept-all pattern is how "every file" is spelled; saying it adds nothing.
    const narrowing = patterns.filter((p) => p !== ACCEPT_ALL_PATTERN);
    if (narrowing.length > 0) {
      parts.push(`when it matches ${narrowing.join(", ")}`);
    }
  } else {
    parts.push(subjectDatasetNoun(entity));
    if (patterns.length === 1 && patterns[0] === ACCEPT_ALL_PATTERN) {
      parts.push("mounting every file");
    } else if (patterns.length > 0) {
      parts.push(`mounting ${patterns.join(", ")}`);
    }
  }
  if (queryNoun !== null) {
    parts.push(`plus ${queryNoun} selected by the query`);
  }
  return parts.join(", ");
}

/**
 * The dataset an event-fired invocation runs on, named from the entity's side.
 * A noun phrase that starts a line, because it is read after a label rather
 * than inside a sentence.
 */
export function subjectDatasetNoun(entity: PlatformEntity | null): string {
  switch (entity) {
    case "file":
      return "The file's whole dataset";
    case "dataset":
      return "The whole dataset";
    default:
      return "The platform event's dataset";
  }
}

function outcomeFor(target: TriggerTargetSpec): TriggerOutcome {
  switch (target.type) {
    case TriggerTargetType.InvokeAction:
      return { verb: "Invoke", target };
    case TriggerTargetType.StartAgent:
      return { verb: "Start agent:", target };
    case TriggerTargetType.SendSlackMessage:
      return { verb: "Notify", target };
  }
}

/**
 * What an action target runs on, for the read view's "Runs on" fact: "Just
 * the file that changed, when it matches **\/*.mcap", or the dataset a schedule
 * is bound to.
 */
export function runsOnPhrase(
  target: InvokeActionTarget,
  firesOn: TriggerSource,
): string {
  const firesOnSchedule = isSchedule(firesOn);
  const files = fileSelectorsOf(target.invocation_input)[0];
  return invocationInputPhrase({
    firesOnSchedule,
    entity: firesOnSchedule ? null : entityOfSubscription(firesOn.events),
    hasDataset:
      !firesOnSchedule && variableRoots(firesOn.events).includes("dataset"),
    passesSession:
      !firesOnSchedule &&
      variableRoots(firesOn.events).includes("session") &&
      isTriggeringSession(target.invocation_input?.sessions),
    oncePerFile:
      !firesOnSchedule && actionTakesTheFile(firesOn.events, firesOn.once_per),
    requiredInputs: target.required_inputs,
    queryNoun: invocationQueryNoun(target.invocation_input),
    dataset: firesOnSchedule ? DatasetBinding.fromQuery(files?.query) : null,
  });
}

/**
 * Whether a second matching occurrence is the same firing or a new one, as
 * the clause after the condition: "Once per file", or "Every time it happens".
 * It follows the condition because the engine checks the condition first, so
 * an occurrence that fails it does not use up its subject's one firing. A
 * schedule has no subject, so it has nothing to say here.
 */
export function readableGrain(source: TriggerSource): TriggerGrain | null {
  if (isSchedule(source)) {
    return null;
  }
  return source.once_per === OncePer.Occurrence
    ? { keyword: "Every", clause: "time it happens" }
    : { keyword: "Once", clause: `per ${source.once_per}` };
}

/** The "when" clause for a firing source: the events, or the schedule in words. */
export function readableSource(source: TriggerSource): string {
  if (isSchedule(source)) {
    return `the schedule ${readableCron(source.cron)} fires`;
  }
  return readableEvents(source.events);
}

/**
 * A cron expression as a sentence — `3 3 3 3 3` → "At 03:03 AM, on day 3 of
 * the month, and on Wednesday, only in March" — or `null` when it is not one.
 */
export function describeCron(
  cron: string,
  options: { use24HourTimeFormat?: boolean } = {},
): string | null {
  if (cron.trim() === "") {
    return null;
  }
  try {
    return cronstrue.toString(cron, options);
  } catch {
    return null;
  }
}

/** `0 9 * * 1` → "at 09:00, only on Monday (UTC)"; the raw expression when it cannot be described. */
export function readableCron(cron: string): string {
  const described = describeCron(cron, { use24HourTimeFormat: true });
  if (described === null) {
    return `\`${cron}\``;
  }
  return `${described.charAt(0).toLowerCase()}${described.slice(1)} (UTC)`;
}

export function summarizeTrigger(
  record: Pick<TriggerRecord, "fires_on" | "condition" | "targets">,
): TriggerSummary {
  return {
    when: readableSource(record.fires_on),
    grain: readableGrain(record.fires_on),
    condition:
      record.condition === null || record.condition === undefined
        ? null
        : readableCondition(record.condition),
    outcomes: record.targets.map(outcomeFor),
  };
}
